import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Button, Paper, TextField, Typography } from '@mui/material';

import {
  PET_LIABILITY_DAMAGED_ITEM,
  useNewPetLiabilityFormDataValue,
} from '../../claimFormData/newClaimFormDataPetLiability';
import {
  useDamagedItemPetLiabilityBackButtonLink,
  useDamagedItemPetLiabilityNextButtonLink,
} from './useDamagedItemPetLiability';

import { BackButton, PageTitle } from '../../../../components/common';
import ButtonContainer from '../../../../components/ButtonContainer';
import { invalidAmountRegex } from '../../../../shared/regex';
import { parseFloatToCents } from '../../../../shared';

import { StyledVerticalContent } from './DamagedItem.styled';

const AMOUNT_MAX_LENGTH = 9;

function DamagedItemPetLiability() {
  const [damagedItem, setDamagedItem] = useNewPetLiabilityFormDataValue(PET_LIABILITY_DAMAGED_ITEM);

  const { item, age, price } = damagedItem || {};

  const history = useHistory();

  const backButtonLink = useDamagedItemPetLiabilityBackButtonLink();
  const nextButtonLink = useDamagedItemPetLiabilityNextButtonLink();

  const handleItemChange = (ev) => setDamagedItem({ ...damagedItem, item: ev?.target?.value });
  const handleAgeChange = (ev) => setDamagedItem({ ...damagedItem, age: ev?.target?.value });
  const handlePriceChange = (value) => setDamagedItem({ ...damagedItem, price: value });

  const isFormValid = !!item && !!age && !!price;

  const [isValidAmount, setIsValidAmount] = useState(true);

  const handleAmountKeyDown = (ev) => {
    const regex = new RegExp(invalidAmountRegex);

    if (regex.test(ev?.key)) {
      ev.preventDefault();
    }
  };

  const handleAmountChange = (ev) => {
    const centsValue = parseFloatToCents(ev?.target?.value);

    if (`${centsValue}`.length > AMOUNT_MAX_LENGTH) return;

    setIsValidAmount(true);
    handlePriceChange(ev?.target?.value);
  };

  const handleAmountBlur = (ev) => {
    const centsValue = parseFloatToCents(ev?.target?.value);
    setIsValidAmount(centsValue !== 0);
    handlePriceChange(ev?.target?.value);
  };

  return (
    <>
      <BackButton to={backButtonLink}>
        <Typography variant="h3">
          <FormattedMessage id="common.button.back" />
        </Typography>
      </BackButton>

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id="pet_liability.new_claim.damaged_item.page_title" />
        </Typography>
      </PageTitle>

      <Paper>
        <StyledVerticalContent>
          <TextField
            fullWidth
            id="item"
            name="item"
            placeholder="Laptop"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="pet_liability.new_claim.damaged_item.form.label.damaged_item" />
              </Typography>
            }
            value={item || ''}
            onChange={handleItemChange}
          />

          <TextField
            fullWidth
            id="age"
            name="age"
            placeholder="1 Jahr"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="pet_liability.new_claim.damaged_item.form.label.damaged_item_age" />
              </Typography>
            }
            value={age || ''}
            onChange={handleAgeChange}
          />

          <TextField
            fullWidth
            id="item-price"
            name="item-price"
            type="number"
            placeholder="2000"
            label={
              <Typography variant="h3" component="span">
                <FormattedMessage id="pet_liability.new_claim.damaged_item.form.label.damaged_item_price" />
              </Typography>
            }
            value={price || ''}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
            onKeyDown={handleAmountKeyDown}
            slotProps={{
              htmlInput: {
                'data-testid': 'input-currency',
                'inputMode': 'numeric',
              },
              input: { endAdornment: '€' },
            }}
            error={!isValidAmount}
            helperText={
              !isValidAmount && (
                <Typography variant="p" color="error">
                  <FormattedMessage id="common.form.error.invalid_amount" />
                </Typography>
              )
            }
          />
        </StyledVerticalContent>
      </Paper>

      <ButtonContainer>
        <Button
          onClick={() => history.push(nextButtonLink)}
          disabled={!isFormValid}
          data-testid="next-button"
        >
          <Typography variant="buttons">
            <FormattedMessage id="common.button.next" />
          </Typography>
        </Button>
      </ButtonContainer>
    </>
  );
}

export default DamagedItemPetLiability;
